import { Box, Button, Card, CardActions, CardContent, CardHeader, Collapse, Stack, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ActionMenu from "../../../components/action-menu/ActionMenu";
import { useTranslation } from "react-i18next";
import { AlarmScheduleListActionEnum, AlarmScheduleListActionItem, AlarmScheduleItem } from "./AlarmScheduleListUtils";
import { CalendarMonth } from "@mui/icons-material";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { isMobile, isTablet } from "react-device-detect";
// import { formatInTimeZone } from "date-fns-tz";
import AlarmScheduleAdministrationUtils from "../AlarmScheduleAdministrationUtils";
import { AlarmScheduleUsageCard } from "./AlarmScheduleUsageCard";
import AlarmScheduleListUtils from "./AlarmScheduleListUtils";
import { IAlarmScheduleLinkedAlarmSchemeModel } from "../../../services/alarm-schedule-data-service/Models";
import React from "react";
import { format } from "date-fns";
import { tz } from "@date-fns/tz";

export type AlarmScheduleListItemProps = {
    item: AlarmScheduleItem,
    onAction: (action: AlarmScheduleListActionEnum, payload: AlarmScheduleItem | IAlarmScheduleLinkedAlarmSchemeModel) => void,
};

export function AlarmScheduleListItem({
    item,
    onAction,
}: AlarmScheduleListItemProps) {

    const [usageExpanded, setUsageExpanded] = useState(false);
    const theme = useTheme();
    const { t } = useTranslation();
    const isPhone = isMobile && !isTablet;


    const actionItems: AlarmScheduleListActionItem[] = useMemo(() => {

        const actions = [{
            label: t("buttons.edit"),
            action: AlarmScheduleListActionEnum.Edit,
            onClick: () => {
                onAction(AlarmScheduleListActionEnum.Edit, item);
            },
        },{
            label: t("buttons.delete"),
            action: AlarmScheduleListActionEnum.Delete,
            onClick: () => {
                onAction(AlarmScheduleListActionEnum.Delete, item);
            },
        }];

        return actions;

    }, [
        t,
        onAction,
        item
    ]);


    function getTitle(): ReactNode {
        return (
            <Box>
                {item.name}
            </Box>
        );
    }

    function getSubHeader(): ReactNode {

        // const dateRange = `Starts ${formatInTimeZone(new Date(item.start), item.olsonTimezone, 'PPp')} and ends ${formatInTimeZone(new Date(item.end), item.olsonTimezone, 'PPp')} ${item.timezone}`;
        const dateRange = `Starts ${format(new Date(item.start), 'PPp', { in: tz(item.olsonTimezone) })} and ends ${format(new Date(item.end), 'PPp', { in: tz(item.olsonTimezone) })} ${item.timezone}`;

        return (
            <Stack
                direction='column'
                sx={{
                    marginLeft: theme.spacing(6),
                    fontSize: '.875rem'
                }}>
                <Box>
                    {dateRange}
                </Box>
                <Box>
                    {`${t('alarmSchedule.duration')}: ${AlarmScheduleAdministrationUtils.getDurationText(item)}`}
                </Box>
                <Box>
                    {`${t('alarmSchedule.repeatInterval')}: ${AlarmScheduleAdministrationUtils.getRepeatIntervalText(item)}`}
                </Box>
                {
                    (item.blackoutSchedules?.length ?? 0) > 0 ?
                    <>
                        {
                            item.blackoutSchedules?.map((o, ndx) => (
                                <React.Fragment key={ndx}>
                                    {
                                        (o.daysOfWeek?.length ?? 0) > 0 ?
                                            <Box>
                                                {`${t('alarmSchedule.blackoutDays')}: ${AlarmScheduleListUtils.getDaysOfWeekText(o.daysOfWeek ?? [])}`}
                                            </Box> :
                                            <Box>
                                                {`${t('alarmSchedule.blackoutDays')}: ${t('alarmSchedule.everyDay')}`}
                                            </Box>
                                    }

                                    {
                                        o.startHour && o.endHour ?
                                            <Box>
                                                {`${t('alarmSchedule.blackoutHours')}: ${o.startHour} to ${o.endHour}`}
                                            </Box> :
                                            <Box>
                                                {`${t('alarmSchedule.blackoutHours')}: ${t('alarmSchedule.allDay')}`}
                                            </Box>
                                        
                                    }
                                </React.Fragment>  
                            ))


                        }

                    </>                        
                    :
                    null
                }
            </Stack>
        );
    }


    function getAvatar() {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(4),
                display: 'flex',
            }}>
                <CalendarMonth fontSize="large"/>
            </Box>
        );
    }


    return (
        <Card variant="outlined" sx={{minHeight: '4rem'}}>
            <CardHeader                sx={{
                    padding: 1,
                    paddingBottom: 0,
                    '& div:nth-of-type(2)': {
                        overflow: isPhone ? 'hidden' : undefined
                    }

                }}
                
                
                title={getTitle()}
                //subheader={getSubHeader()}
                avatar={getAvatar()}
                action={<ActionMenu actionItems={actionItems} />}
                
            />
            

                <CardContent sx={{
                    padding: '0!important',
                    margin: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                    minHeight: theme.spacing(4),
                    overflow: 'hidden',
                }}>

                    {getSubHeader()}


                    {item.linkedAlarmSchemes.length > 0 && 
            <>
                <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>

                    <Button
                        sx={{
                            color: "text.primary"
                        }}
                        variant="text"
                        onClick={() => { setUsageExpanded(!usageExpanded)}
                        }
                        style={{ marginLeft: "auto" }}
                        endIcon={<ExpandMoreIcon sx={{
                            transform: !usageExpanded ? "rotate(0deg)" : "rotate(180deg)",
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.shortest,
                            })
                        }}
                        />}
                    >
                        {t('objectWithCount', {
                            name: getObjectTypeName(eLynxObjectTypeEnum.AlarmScheme, true),
                            count: item.linkedAlarmSchemes.length
                        })}
                    </Button>
                </CardActions>

                <Collapse in={usageExpanded} timeout="auto">

                    <AlarmScheduleUsageCard 
                        items={item.linkedAlarmSchemes} 
                        onAction={(action, payload) => { onAction && onAction(action, payload)}}
                    />

                </Collapse>
            </>
            }

            </CardContent>


        </Card>
    );
}
